import type { AgentToolDetail } from '@onefootprint/request-types/dashboard';
import { enabledToolKeys, isToolAvailable, toolKeyForCatalogKind } from '@/utils/vendor-kind';
import type { Bundle } from '../../store/types';
import { isDecisionNode, isRulesNode, isTaskNode } from '../../store/types';
import { getSkillExcerpt } from '../skills-view/skill-tree';
import { skillNames } from './reference-index';

export type PromptMentionKind = 'task' | 'input' | 'output' | 'tool' | 'skill';

export type PromptMentionItem = {
  key: string;
  kind: PromptMentionKind;
  label: string;
  sublabel?: string;
  /** Plain text spliced into the prompt in place of the @query. */
  insertText: string;
};

export type PromptMentionSource = {
  bundle: Bundle;
  toolCatalog: AgentToolDetail[];
};

// A prompt can only usefully reference work that happens before it runs, so when the
// editor belongs to a node we cut tasks/outputs off at that node. Editors that aren't
// tied to a node (norms) see the whole graph.
export const buildPromptMentionItems = (
  { bundle, toolCatalog }: PromptMentionSource,
  currentNodeId?: string,
): PromptMentionItem[] => {
  const cutoff = currentNodeId ? bundle.nodes.findIndex(n => n.id === currentNodeId) : -1;
  const visibleNodes = cutoff >= 0 ? bundle.nodes.slice(0, cutoff) : bundle.nodes;

  const tasks = visibleNodes.filter(isTaskNode).flatMap(node => {
    if (!node.name.trim()) return [];
    return [{ key: `task-${node.id}`, kind: 'task' as const, label: node.name, insertText: node.name }];
  });

  const inputs = bundle.inputs.flatMap(input => {
    if (!input.name.trim()) return [];
    return [
      {
        key: `input-${input.id}`,
        kind: 'input' as const,
        label: `inputs.${input.name}`,
        sublabel: input.description || undefined,
        insertText: `inputs.${input.name}`,
      },
    ];
  });

  // Same addressing scheme the rules/CEL autocomplete uses (see store/autocomplete.ts):
  // task judgment fields and rules keys are both referenced as `outputs.<name>`.
  const outputs = visibleNodes.flatMap(node => {
    if (isTaskNode(node)) {
      return node.fields.flatMap(field => {
        if (!field.name.trim()) return [];
        return [
          {
            key: `output-${node.id}-${field.id}`,
            kind: 'output' as const,
            label: `outputs.${field.name}`,
            sublabel: node.name || undefined,
            insertText: `outputs.${field.name}`,
          },
        ];
      });
    }
    if (isRulesNode(node) && node.key.trim()) {
      return [
        {
          key: `output-${node.id}`,
          kind: 'output' as const,
          label: `outputs.${node.key}`,
          sublabel: node.name || undefined,
          insertText: `outputs.${node.key}`,
        },
      ];
    }
    if (isDecisionNode(node)) {
      return node.questions
        .filter(question => question.key.trim())
        .map(question => ({
          key: `output-${node.id}-${question.key}`,
          kind: 'output' as const,
          label: `outputs.${question.key}`,
          sublabel: node.name || undefined,
          insertText: `outputs.${question.key}`,
        }));
    }
    return [];
  });

  // Only offer tools enabled on this agent — mentioning a tool the agent can't call
  // would point the model at something it doesn't have.
  const enabledKeys = enabledToolKeys(bundle.tools);
  const tools = toolCatalog.flatMap(tool => {
    if (!isToolAvailable(tool) || !enabledKeys.has(toolKeyForCatalogKind(tool.kind))) return [];
    return [
      {
        key: `tool-${tool.kind}`,
        kind: 'tool' as const,
        label: tool.displayName,
        sublabel: tool.description,
        insertText: tool.displayName,
      },
    ];
  });

  const names = new Set(skillNames(bundle.skills));
  const skills = bundle.skills.flatMap((node, index) => {
    if (!names.has(node.name)) return [];
    return [
      {
        key: `skill-${node.name}`,
        kind: 'skill' as const,
        label: node.name,
        sublabel: getSkillExcerpt(node, String(index)) || undefined,
        insertText: node.name,
      },
    ];
  });

  return [...tasks, ...inputs, ...outputs, ...tools, ...skills];
};

export const filterPromptMentionItems = (items: PromptMentionItem[], query: string): PromptMentionItem[] => {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter(item => item.label.toLowerCase().includes(q) || (item.sublabel ?? '').toLowerCase().includes(q));
};
